// АНИМ.4: модалка повышения уровня аккаунта.
//
// Показывается ПОСЛЕ EndOfMatchModal (а до LeagueTransitionModal) если
// в level_ups[] есть milestone-уровень (10/25/50/100). Для обычных
// уровней — только embedded strip в EndOfMatchModal (АНИМ.3).
//
// Также может вызываться извне при unclaimed level-up (Профиль и др.),
// сейчас не используется — оставлено для будущего.
//
// Композиция:
//   - тёмный фон + лёгкий vignette
//   - сверху: caption «УРОВЕНЬ»
//   - центр: большое число level — pop-in scale + амбер bloom
//   - под числом: progress-bar 0% → 100% (за 0.8с)
//   - на milestone: дополнительная фракционная вспышка + лор-цитата
//   - снизу: RewardCascade с наградами
//   - в конце: кнопка «ПРИНЯТЬ»
//
// Multi-LU (например 8 → 11): показываем финальное число (11),
// одометр прыгает через промежуточные за 200мс/уровень. Награды
// показываются одним списком в конце.

import React from 'react';
import { Stage, T, Easing, useTime } from '../../components/animation/animations';
import { useAnimationsEnabled } from '../../hooks/useAnimationsEnabled';
import { useTr } from '../../i18n/useTr';
import { play } from '../../audio/sfx';
import { RewardCascade, CTAForward, hexA, type RewardChipData } from '../LeagueTransition/parts';
import type { Faction } from '../../engine-bridge';

const COLORS = {
  cy: '#3DF0FF', amber: '#FFB13D', lime: '#9CFF3D',
  md: '#A0A4B4', lo: '#60657A', hi: '#F4F6FB'
};

// Цвета фракций для milestone-flash.
const FACTION_COLORS: Record<Faction, string> = {
  deus: '#3D7DFF',
  syntetics: '#C93DFF',
  global: '#FF8A1F',
  livlife: '#FF3D3D',
  neutral: '#A0A4B4'
};

// Лор-цитаты для milestone-уровней.
const MILESTONE_QUOTES_RU: Record<number, string> = {
  10:  'Тебя начали замечать.',
  25:  'Сеть запомнила твой след.',
  50:  'Половина пути. Назад дороги нет.',
  100: 'Сотый. Корпорат до мозга костей.'
};
const MILESTONE_QUOTES_EN: Record<number, string> = {
  10:  'They started to notice you.',
  25:  'The net remembered your trace.',
  50:  'Halfway. No way back.',
  100: 'The hundredth. Corp to the bone.'
};

const MILESTONES = [10, 25, 50, 100];
export function isMilestone(level: number): boolean {
  return MILESTONES.includes(level);
}

export interface LevelUpModalProps {
  // Массив новых уровней (например [9, 10] = поднялся с 8 на 10).
  levelUps: number[];
  // Награды с сервера (account.rewards — массив строк).
  rewards: string[];
  // Текущая фракция игрока (для milestone-flash).
  faction: Faction;
  onClose: () => void;
}

export function LevelUpModal(props: LevelUpModalProps) {
  const enabled = useAnimationsEnabled();
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';

  // Этап 71: звук повышения уровня (один раз при появлении модалки).
  React.useEffect(() => {
    if (props.levelUps.length > 0) play('levelUp');
  }, []);

  if (props.levelUps.length === 0) return null;

  const fromLevel = props.levelUps[0] - 1;
  const toLevel = props.levelUps[props.levelUps.length - 1];
  const hasMilestone = props.levelUps.some(isMilestone);
  const milestoneLevel = props.levelUps.find(isMilestone) ?? null;
  const duration = hasMilestone ? 5.0 : 3.0;
  const factionColor = FACTION_COLORS[props.faction] || COLORS.amber;

  const chips = React.useMemo<RewardChipData[]>(
    () => parseRewards(props.rewards),
    [props.rewards]
  );

  const quote = milestoneLevel
    ? (isEn
        ? MILESTONE_QUOTES_EN[milestoneLevel]
        : MILESTONE_QUOTES_RU[milestoneLevel])
    : null;

  if (!enabled) {
    return (
      <NoAnimRenderer
        fromLevel={fromLevel}
        toLevel={toLevel}
        chips={chips}
        quote={quote || null}
        onClose={props.onClose}
      />
    );
  }

  return (
    <Stage width={720} height={1280} duration={duration} background="#050509">
      <Scene
        fromLevel={fromLevel}
        toLevel={toLevel}
        chips={chips}
        hasMilestone={hasMilestone}
        quote={quote}
        factionColor={factionColor}
        onClose={props.onClose}
      />
    </Stage>
  );
}

function Scene({
  fromLevel, toLevel, chips, hasMilestone, quote, factionColor, onClose
}: {
  fromLevel: number; toLevel: number; chips: RewardChipData[];
  hasMilestone: boolean; quote: string | null; factionColor: string;
  onClose: () => void;
}) {
  const t = useTime();
  const tr = useTr();

  // Тайминги:
  //   0.0–0.3   вход (vignette + caption)
  //   0.3–0.5   pop старого числа
  //   0.5–1.1   одометр fromLevel → toLevel
  //   1.1–1.9   progress-bar 0 → 100%
  //   1.9–2.3   milestone-flash (если есть)
  //   2.3–3.0   лор-цитата (если milestone)
  //   3.0–4.0   rewards cascade
  //   4.5+      CTA
  const captionOp = T(t, 0.0, 0.3, 0, 1, Easing.easeOutCubic);
  const numberPopScale = T(t, 0.3, 0.55, 0, 1.15, Easing.easeOutCubic) -
                         T(t, 0.55, 0.7, 0, 0.15, Easing.easeOutCubic);
  // Одометр: floor(T(t, ...)) — целочисленный counter.
  const odoStart = 0.5;
  const odoEnd = Math.min(1.1, odoStart + (toLevel - fromLevel) * 0.18);
  const odoValue = Math.round(T(t, odoStart, odoEnd, fromLevel, toLevel, Easing.easeOutCubic));

  const barProgress = T(t, 1.1, 1.9, 0, 1, Easing.easeOutCubic);
  const flashOp = hasMilestone
    ? T(t, 1.9, 2.1, 0, 0.7, Easing.easeOutCubic) -
      T(t, 2.1, 2.5, 0, 0.7, Easing.easeInCubic)
    : 0;
  const quoteOp = hasMilestone
    ? T(t, 2.5, 3.0, 0, 1, Easing.easeOutCubic)
    : 0;

  const ctaAt = hasMilestone ? 4.2 : 2.4;
  const rewardsAt = hasMilestone ? 3.2 : 2.0;

  return (
    <>
      {/* Vignette */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(ellipse at 50% 50%, transparent 0%, ${hexA('#000', 0.6)} 80%)`,
        opacity: T(t, 0, 0.3, 0, 1, Easing.easeOutCubic)
      }} />

      {/* Milestone fraction flash (поверх всего, mix-blend) */}
      {hasMilestone && (
        <div style={{
          position: 'absolute', inset: 0,
          background: `radial-gradient(ellipse at 50% 50%, ${hexA(factionColor, 0.6)} 0%, transparent 60%)`,
          opacity: Math.max(0, flashOp),
          mixBlendMode: 'screen' as React.CSSProperties['mixBlendMode'],
          pointerEvents: 'none'
        }} />
      )}

      {/* Caption «УРОВЕНЬ» */}
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 360,
        textAlign: 'center', opacity: captionOp,
        color: COLORS.md, fontFamily: "'JetBrains Mono', monospace",
        fontSize: 14, letterSpacing: '0.3em', fontWeight: 500
      }}>{tr('УРОВЕНЬ', 'LEVEL')}</div>

      {/* Big number */}
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 440,
        textAlign: 'center',
        color: COLORS.amber,
        fontFamily: "'Chakra Petch', sans-serif",
        fontSize: 180, fontWeight: 700, lineHeight: 1,
        transform: `scale(${1 + Math.max(0, numberPopScale) * 0.15})`,
        textShadow: `0 0 36px ${hexA(COLORS.amber, 0.6)}, 0 0 64px ${hexA(COLORS.amber, 0.3)}`,
        opacity: T(t, 0.3, 0.5, 0, 1, Easing.easeOutCubic)
      }}>{odoValue}</div>

      {/* Progress bar */}
      <div style={{
        position: 'absolute', left: 120, right: 120, top: 720,
        height: 8,
        background: hexA(COLORS.amber, 0.15),
        opacity: T(t, 1.0, 1.2, 0, 1, Easing.easeOutCubic),
        clipPath: 'polygon(0 0, calc(100% - 4px) 0, 100% 50%, calc(100% - 4px) 100%, 0 100%, 4px 50%)'
      }}>
        <div style={{
          height: '100%',
          width: `${barProgress * 100}%`,
          background: `linear-gradient(90deg, ${COLORS.amber} 0%, ${hexA(COLORS.amber, 0.8)} 100%)`,
          boxShadow: `0 0 12px ${COLORS.amber}`
        }} />
      </div>

      {/* Milestone quote */}
      {hasMilestone && quote && (
        <div style={{
          position: 'absolute', left: 0, right: 0, top: 780,
          textAlign: 'center', opacity: quoteOp,
          color: factionColor,
          fontFamily: "'Inter', sans-serif",
          fontStyle: 'italic',
          fontSize: 18, letterSpacing: '0.02em',
          textShadow: `0 0 12px ${hexA(factionColor, 0.5)}`,
          padding: '0 40px'
        }}>{quote}</div>
      )}

      {/* Rewards cascade */}
      {chips.length > 0 && (
        <RewardCascade rewards={chips} startAt={rewardsAt} y={hasMilestone ? 880 : 820} />
      )}

      <CTAForward
        at={ctaAt}
        label={tr('ПРИНЯТЬ', 'CLAIM')}
        color={COLORS.amber}
        onClick={onClose}
      />
    </>
  );
}

function NoAnimRenderer({
  fromLevel: _fromLevel, toLevel, chips, quote, onClose
}: {
  fromLevel: number; toLevel: number; chips: RewardChipData[];
  quote: string | null; onClose: () => void;
}) {
  const tr = useTr();
  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#050509',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 16, padding: 40, zIndex: 100000
    }}>
      <div style={{
        color: COLORS.md, fontFamily: "'JetBrains Mono', monospace",
        fontSize: 14, letterSpacing: '0.3em'
      }}>{tr('УРОВЕНЬ', 'LEVEL')}</div>
      <div style={{
        color: COLORS.amber, fontFamily: "'Chakra Petch', sans-serif",
        fontSize: 140, fontWeight: 700, lineHeight: 1,
        textShadow: `0 0 24px ${hexA(COLORS.amber, 0.6)}`
      }}>{toLevel}</div>
      {quote && (
        <div style={{
          color: COLORS.amber, fontFamily: "'Inter', sans-serif",
          fontStyle: 'italic', fontSize: 18, textAlign: 'center',
          padding: '0 40px'
        }}>{quote}</div>
      )}
      {chips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
          {chips.map((c, i) => (
            <div key={i} style={{
              padding: '8px 14px',
              border: `1px solid ${c.color}`, color: c.color,
              fontFamily: "'JetBrains Mono', monospace", fontSize: 13
            }}>{c.icon} {c.label}</div>
          ))}
        </div>
      )}
      <button
        onClick={onClose}
        style={{
          marginTop: 12, padding: '14px 60px',
          background: hexA(COLORS.amber, 0.12),
          border: `1.5px solid ${COLORS.amber}`,
          color: COLORS.amber,
          fontFamily: "'Chakra Petch', sans-serif", fontSize: 18,
          fontWeight: 700, letterSpacing: '0.15em', cursor: 'pointer'
        }}
      >{tr('ПРИНЯТЬ', 'CLAIM')}</button>
    </div>
  );
}

// Серверная account.rewards — это массив строк типа «+1 Основной ШК»,
// «+50 Скр», «Титул: Корпорат». Парсим в чипы с иконкой по типу.
function parseRewards(rewards: string[]): RewardChipData[] {
  return rewards.map((r) => {
    const lower = r.toLowerCase();
    if (lower.includes('скр')) return { label: r, color: COLORS.cy, icon: '§' };
    if (lower.includes('шк') || lower.includes('контейнер'))
      return { label: r, color: COLORS.amber, icon: '▣' };
    if (lower.includes('титул')) return { label: r, color: COLORS.amber, icon: '☰' };
    if (lower.includes('геро')) return { label: r, color: COLORS.lime, icon: '★' };
    return { label: r, color: COLORS.md, icon: '•' };
  });
}
